import CheckCircle from "@mui/icons-material/CheckCircle";
import CircleOutlined from "@mui/icons-material/CircleOutlined";
import PendingOutlined from "@mui/icons-material/PendingOutlined";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  Skeleton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { GetWasteShipmentQuery, Issue, IssueLevel, UnitType } from "generated-graphql/graphql";
import pluralize from "pluralize";

import { formatQuantity } from "../Containers/containerDisplay";
import { attachedDocumentAmountLabel, missingManifestTotalReason } from "./reconciliationDisplay";
import { distinctStreamCount, formatTotalsByUnit } from "./shipmentDisplay";
import { LifecycleState, LifecycleStepKey, lifecycleSteps } from "./shipmentLifecycle";
import { ShipmentReconciliationChip } from "./ShipmentReconciliationChip";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/** Marker size, matching the spec's 20px glyph in the progress track. */
const STEP_ICON_SIZE = 20;

function StepIcon({ state }: { state: LifecycleState }) {
  if (state === "done") {
    return <CheckCircle color="success" sx={{ fontSize: STEP_ICON_SIZE }} />;
  }
  if (state === "current") {
    // Amber and mid-motion: the step the shipment is sitting on, as distinct
    // from one already closed out. Outlined, not filled — a solid marker reads
    // as done, which is the one thing this step is not.
    return <PendingOutlined color="warning" sx={{ fontSize: STEP_ICON_SIZE }} />;
  }
  if (state === "inactive") {
    // A step that will never happen, as distinct from one that has not happened
    // yet — a shipment arranged by phone never produces a request document. The
    // same dash Scout's todo list uses for a skipped item.
    return <RemoveCircleOutline sx={{ fontSize: STEP_ICON_SIZE, color: "text.disabled" }} />;
  }
  if (state === "terminal") {
    // A dashed ring rather than a filled or empty one: the step was reached
    // and closed, but not completed.
    return (
      <Box
        sx={{
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: "50%",
          border: "2px dashed",
          borderColor: "text.disabled",
        }}
      />
    );
  }
  return <CircleOutlined sx={{ fontSize: STEP_ICON_SIZE, color: "text.disabled" }} />;
}

/**
 * The page's interruptions: what blocks shipping, and whether the vendor is
 * holding a document that no longer describes the load.
 *
 * Full width above the cards rather than inside one of them, because either
 * can be caused by a header field or by a container — pinning them beside one
 * or the other would be a guess. A shipment that cannot ship has to say so
 * before the reader starts scrolling.
 */
export function ShipmentAlerts({
  issues,
  onResendRequest,
  resendBlockedReason,
  requestStale = false,
  requestDocumentExists = false,
}: {
  issues: Issue[];
  onResendRequest?: () => void;
  /**
   * Why re-sending is unavailable, or undefined when it is. The request is
   * built from saved state, so offering it mid-edit would hand the vendor a
   * document that does not match what the user is looking at.
   */
  resendBlockedReason?: string;
  /**
   * Whether the vendor is holding a request that no longer describes the load.
   *
   * The page decides, because it is not simply `manifestRequestStale`: once a
   * shipment has shipped or been canceled the vendor's copy cannot be brought
   * back into line, so saying it is out of date only invites an action that no
   * longer exists.
   */
  requestStale?: boolean;
  /**
   * Whether a request document was ever produced.
   *
   * A request made by phone, or through the vendor's portal, or simply recorded
   * as already made, stamps the fingerprint that makes a shipment go stale but
   * renders no document — so there is no copy in the vendor's hands to describe
   * as out of date, and nothing to re-send. The progress rail draws the same
   * distinction on its own button.
   */
  requestDocumentExists?: boolean;
}) {
  const blocking = issues.filter((issue) => issue.level === IssueLevel.Critical);

  if (blocking.length === 0 && !requestStale) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {blocking.length > 0 && (
        <Alert severity="error">
          <AlertTitle sx={{ mb: 0.25 }}>
            {blocking.length} {pluralize("issue", blocking.length)}{" "}
            {blocking.length === 1 ? "blocks" : "block"} shipping
          </AlertTitle>
          {blocking.map((issue, index) => (
            <Typography key={`${issue.key}-${index}`} variant="body2">
              {issue.message}
            </Typography>
          ))}
        </Alert>
      )}

      {requestStale && (
        <Alert
          severity="warning"
          action={
            onResendRequest ? (
              <Tooltip title={resendBlockedReason ?? ""}>
                <span>
                  <Button
                    size="small"
                    onClick={onResendRequest}
                    disabled={Boolean(resendBlockedReason)}
                  >
                    {requestDocumentExists ? "Send updated request" : "Send request"}
                  </Button>
                </span>
              </Tooltip>
            ) : undefined
          }
        >
          <AlertTitle sx={{ mb: 0.25 }}>
            {requestDocumentExists
              ? "The vendor's copy is out of date"
              : "The request is out of date"}
          </AlertTitle>
          <Typography variant="body2">
            {requestDocumentExists
              ? "This shipment changed after the request document was produced, so the vendor is holding a copy that no longer describes what is shipping."
              : "This shipment changed after the pickup was requested, so the vendor is expecting a load that no longer matches what is shipping."}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
}

/** One tinted figure panel: a quiet label over the number it names. */
function MetricPanel({
  label,
  children,
  sx,
}: {
  label: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ bgcolor: "grey.50", borderRadius: 1, px: 2, py: 1, ...sx }}>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * The shipment's headline numbers.
 *
 * Only the numbers now. Which shipment this is, what state it is in and where
 * it came from moved to the page header, which stays on screen — a card that
 * scrolls away is a poor place to keep the thing that says what you are
 * looking at.
 *
 * The requested and manifest totals share one panel, split by a rule, because
 * they are the two halves of a single comparison — the question the page is open
 * to answer. The verdict chip sits on that rule, so it reads as a statement
 * about those two numbers and not as a second status for the shipment.
 *
 * Counts sit apart from the comparison: they describe the load either way and do
 * not change when a manifest lands.
 */
export function ShipmentInformationCard({ shipment }: { shipment: Shipment }) {
  const containerCount = shipment.lines.length;
  const streamCount = distinctStreamCount(shipment.lines);
  const attached = shipment.manifestReceivedAt != null;

  // Taken from the server's total rather than summed from the quantity diffs:
  // diffs exist only where the two sides disagree, so a matching document would
  // have shown an em dash and a partly-matching one would have shown only the
  // differing streams' weight.
  const documentTotal = shipment.reconciliation.attachedDocumentTotalPounds;
  const documentAmountLabel = attachedDocumentAmountLabel(
    shipment.reconciliation.attachedDocumentKind
  );

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}
        sx={{ mb: 1.5 }}
      >
        <Typography
          component="p"
          variant="overline"
          color="text.secondary"
          sx={{ m: 0, letterSpacing: "1px" }}
        >
          Shipment information
        </Typography>
        {/* A shipment reconstructed from its manifest rather than packaged in
            Encamp. It qualifies the figures below, which is why it stayed with
            them when the identity moved to the page header. */}
        {shipment.backfilledFromManifest && (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            Backfilled from manifest
          </Typography>
        )}
      </Stack>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricPanel
            label={attached ? "Requested amount" : "Total amount"}
            sx={{ height: "100%" }}
          >
            <Typography variant="h5" component="p">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </Typography>
            {/* Only once there is a manifest to compare against. Before that the
                panel is one figure, not a comparison with an empty side. */}
            {attached && (
              <>
                <Divider sx={{ my: 1 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" component="p">
                      {documentAmountLabel}
                    </Typography>
                    {documentTotal == null ? (
                      // Why there is no number, not an empty row. A bare label
                      // reads as a spacing gap, and the dangling case has no
                      // chip beside it to explain the blank either.
                      <Typography variant="body2" color="text.disabled" fontStyle="italic">
                        {missingManifestTotalReason(shipment)}
                      </Typography>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        {formatQuantity(documentTotal, UnitType.Pounds)}
                      </Typography>
                    )}
                  </Box>
                  <ShipmentReconciliationChip status={shipment.reconciliation.status} />
                </Stack>
              </>
            )}
          </MetricPanel>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <MetricPanel label={pluralize("Waste stream", streamCount)}>
              <Typography variant="h5" component="p">
                {streamCount}
              </Typography>
            </MetricPanel>
            <MetricPanel label={pluralize("Container", containerCount)}>
              <Typography variant="h5" component="p">
                {containerCount}
              </Typography>
            </MetricPanel>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

/**
 * The page's outer surface: a canvas the section cards sit on.
 *
 * Shared by the loaded page and its skeleton so the two cannot drift — a
 * skeleton on a different background is a visible flash at the moment the data
 * lands, which is exactly when the user is looking.
 */
export function ShipmentPageShell({
  header,
  children,
}: {
  /**
   * The sticky page header, rendered outside the padded column so it can carry
   * its own padding and reach both edges — a rule that stops short of the
   * viewport reads as a card's border rather than as the header's own.
   */
  header?: React.ReactNode;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: "canvas",
        // Fill below the app bar, or the canvas stops where the content does
        // and the page ends in a band of white.
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      {header}
      <Stack spacing={3} sx={{ py: 2, px: 3 }}>
        {children}
      </Stack>
    </Box>
  );
}

/**
 * The two-column body: the rail leading, contents beside it, and the rail above
 * them when narrow.
 *
 * The rail is on the left because progress is what the page is read in order of
 * — where the shipment has got to, then what it holds. That also makes its
 * placement consistent across widths: it comes first at every size rather than
 * jumping from above the contents to their right.
 *
 * `body` stays first in the DOM. Grid areas place the rail visually without
 * moving the page's main content behind it in reading order.
 */
export function ShipmentPageGrid({ body, rail }: { body: React.ReactNode; rail: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        // Rows stretch rather than hug their content, so the rail reaches the
        // bottom of the cards beside it and the whole block above Contents
        // ends on one line. Cards that hug leave a ragged edge across the top
        // half, which reads as an alignment mistake rather than as three
        // panels of different lengths.
        alignItems: "stretch",
        gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)", lg: "290px minmax(0, 1fr)" },
        gridTemplateAreas: { xs: '"rail" "body"', md: '"rail body"' },
      }}
    >
      <Stack spacing={3} sx={{ gridArea: "body", minWidth: 0 }}>
        {body}
      </Stack>
      <Box sx={{ gridArea: "rail", minWidth: 0, display: "flex" }}>{rail}</Box>
    </Box>
  );
}

/**
 * The page's shape before its data arrives.
 *
 * Deliberately the same shell, the same grid and the same card rhythm as the
 * loaded page: a skeleton that guesses at a different layout makes the content
 * jump into place rather than resolve.
 */
export function ShipmentDetailSkeleton() {
  // `flexGrow` for the same reason the real cards carry it: the grid rows
  // stretch, and a card that hugs its content would make the skeleton's top
  // half end ragged where the loaded page's ends level.
  const card = (children: React.ReactNode) => (
    // minWidth as on the loaded page's cards, for the same reason: these are
    // flex items and would otherwise refuse to shrink below their content.
    <Paper elevation={0} sx={{ p: 3, flexGrow: 1, minWidth: 0 }}>
      {children}
    </Paper>
  );
  // Every pane's heading is the same overline, so every placeholder for one is
  // the same size.
  const heading = <Skeleton variant="text" width={140} height={16} sx={{ mb: 1.5 }} />;
  return (
    <ShipmentPageShell
      header={
        // The same two lines and the same rule the real header draws, so the
        // page does not gain a band across its top as it resolves.
        <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Skeleton variant="text" width={190} height={30} />
            <Skeleton variant="rounded" width={72} height={24} />
          </Stack>
          <Skeleton variant="text" width={420} height={20} />
        </Box>
      }
    >
      <ShipmentPageGrid
        rail={card(
          <>
            {heading}
            {/* Four steps with their markers, not a list of facts: the rail is
                a progress track, and a column of label/value pairs resolves
                into something a different shape. */}
            <Stack>
              {Array.from({ length: 4 }).map((_, i) => (
                // `stretch`, as the real track does it: the hairline grows to
                // fill the row, and with `flex-start` it has no height to grow
                // into and the steps read as four loose markers.
                <Stack key={i} direction="row" spacing={1} alignItems="stretch">
                  <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                    <Skeleton variant="circular" width={20} height={20} />
                    {i < 3 && <Box sx={{ flexGrow: 1, width: "1px", bgcolor: "divider" }} />}
                  </Stack>
                  <Box sx={{ minWidth: 0, pb: i < 3 ? 2 : 0 }}>
                    <Skeleton variant="text" width={110} height={18} />
                    <Skeleton variant="text" width={84} height={14} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        body={
          <>
            {/* Shipment information: the same card, heading and panel grid as
                the real one, so the figures resolve in place instead of the
                block changing shape.

                The heading alone above them. The request number and status chip
                went to the page header when the identity moved there, and this
                skeleton draws them in its own header — standing them in here as
                well left three placeholder lines that vanished on load, which
                shrank the card and moved everything under it. */}
            <Paper elevation={0} sx={{ px: 3, py: 2 }}>
              <Skeleton variant="text" width={140} height={16} sx={{ mb: 1.5 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Skeleton variant="rounded" height={116} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <Skeleton variant="rounded" height={50} />
                    <Skeleton variant="rounded" height={50} />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Full width, matching the loaded page: the manifest is a step in
                the rail now, not a card sharing this row. */}
            {card(
              <>
                {heading}
                <Grid container spacing={2}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6 }}>
                      <Skeleton variant="text" width={90} height={14} />
                      <Skeleton variant="text" width={140} height={20} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        }
      />

      {card(
        <>
          {heading}
          {/* The grid's own header row plus a few rows of it, rather than one
              block: Contents resolves into a table and a solid rectangle would
              be a different shape settling into place. */}
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Stack spacing={1}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={44} />
            ))}
          </Stack>
        </>
      )}
    </ShipmentPageShell>
  );
}

/**
 * Where the shipment has got to, beside what it is rather than above it.
 *
 * Progress carries the next step, because the next step IS the progress —
 * keeping them together is what lets the body be about the waste. The
 * request's own fields are deliberately not here: they live in the body's
 * Details card in every status, so they never move as the shipment ages. Nor
 * is the manifest, which is a fact about the shipment and sits with the other
 * facts in the body.
 */
export function ShipmentRail({
  shipment,
  stepActions,
}: {
  shipment: Shipment;
  /**
   * The action that advances a given step, rendered beneath that step's own
   * label. Keyed rather than passed as one block: an action detached from the
   * step it advances leaves the reader to work out which is which, and the
   * order of two buttons in a row does not say it.
   */
  stepActions?: Partial<Record<LifecycleStepKey, React.ReactNode>>;
}) {
  const steps = lifecycleSteps(shipment);

  return (
    // `minWidth: 0` is load-bearing: this is a flex item, so it defaults to
    // `min-width: auto` and refuses to shrink below its widest unbreakable
    // child. A long manifest tracking number then pushed the whole card past
    // its 240px grid track and over the cards beside it.
    <Paper elevation={0} sx={{ p: 3, flexGrow: 1, minWidth: 0 }}>
      <Typography
        component="p"
        variant="overline"
        color="text.secondary"
        sx={{ m: 0, mb: 2, letterSpacing: "1px" }}
      >
        Progress
      </Typography>
      <Stack>
        {steps.map((step, index) => {
          const action = stepActions?.[step.key];
          const last = index === steps.length - 1;
          return (
            <Stack key={step.key} direction="row" spacing={1} alignItems="stretch">
              {/* Icon over a hairline that reaches the next step's icon, so the
                    column reads as one track rather than four loose markers. The
                    last step has nothing to connect to. */}
              <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                <StepIcon state={step.state} />
                {!last && (
                  <Box sx={{ flexGrow: 1, width: "1px", bgcolor: "divider", minHeight: 8 }} />
                )}
              </Stack>
              <Box sx={{ minWidth: 0, pb: last ? 0 : 2 }}>
                <Typography
                  variant="subtitle2"
                  color={
                    // Only a step that happened, or is happening, is stated at
                    // full strength. A skipped one used to render bolder than a
                    // pending one, which read as the more important of the two.
                    step.state === "done" || step.state === "current"
                      ? "text.primary"
                      : "text.disabled"
                  }
                >
                  {step.label}
                </Typography>
                {step.meta && (
                  <Typography variant="caption" color="text.secondary" component="p">
                    {step.meta}
                  </Typography>
                )}
                {action && <Box sx={{ mt: 1 }}>{action}</Box>}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
